import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiCheckCircle, FiXCircle, FiLoader, FiMail, FiCpu, FiRefreshCw, FiSave, 
  FiEye, FiEyeOff, FiSettings, FiBell, FiShield, FiAlertTriangle, FiTrash2,
  FiPlus, FiEdit2, FiGlobe, FiMonitor
} from 'react-icons/fi';
import './Settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('connections');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Настройки подключений
  const [connectionSettings, setConnectionSettings] = useState({
    email_host: '',
    email_port: 465,
    email_use_ssl: true,
    email_host_user: '',
    email_host_password: '',
    ai_api_url: 'https://ask.chadgpt.ru/api/public/deepseek-v3.1',
    openai_api_key: '',
  });
  const [passwordDisplay, setPasswordDisplay] = useState('');
  const [apiKeyDisplay, setApiKeyDisplay] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // Настройки уведомлений
  const [notificationSettings, setNotificationSettings] = useState({
    notification_email: '',
    notify_hack_attempts: true,
    notify_server_errors: true,
    notify_email_errors: true,
    notify_ai_errors: true,
    notify_transcription_errors: true,
  });
  
  // Настройки интерфейса
  const [interfaceSettings, setInterfaceSettings] = useState({
    logo: null,
    website_url: '',
    primary_color: '#ff6b35',
    secondary_color: '#ff8c5a',
    accent_color: '#ffa07a',
    timestamp_theory_color: '#d1ecf1',
    timestamp_practice_color: '#fff3cd',
    timestamp_theory_badge_color: '#17a2b8',
    timestamp_practice_badge_color: '#ffc107',
  });
  
  // Блэклист IP
  const [ipBlacklist, setIpBlacklist] = useState([]);
  const [newIp, setNewIp] = useState({ ip_address: '', reason: '' });
  const [editingIp, setEditingIp] = useState(null);
  
  // Неудачные попытки входа
  const [failedAttempts, setFailedAttempts] = useState([]);
  
  // Статусы проверки подключений
  const [emailTestStatus, setEmailTestStatus] = useState(null);
  const [aiTestStatus, setAiTestStatus] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingAI, setTestingAI] = useState(false);

  useEffect(() => {
    fetchAllSettings();
    if (activeTab === 'ip-blacklist') {
      fetchIpBlacklist();
    }
    if (activeTab === 'failed-logins') {
      fetchFailedAttempts();
    }
  }, [activeTab]);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/settings/system/');
      const data = response.data;
      
      setConnectionSettings({
        email_host: data.email_host || '',
        email_port: data.email_port || 465,
        email_use_ssl: data.email_use_ssl !== undefined ? data.email_use_ssl : true,
        email_host_user: data.email_host_user || '',
        email_host_password: '',
        ai_api_url: data.ai_api_url || 'https://ask.chadgpt.ru/api/public/deepseek-v3.1',
        openai_api_key: '',
      });
      
      setNotificationSettings({
        notification_email: data.notification_email || '',
        notify_hack_attempts: data.notify_hack_attempts !== undefined ? data.notify_hack_attempts : true,
        notify_server_errors: data.notify_server_errors !== undefined ? data.notify_server_errors : true,
        notify_email_errors: data.notify_email_errors !== undefined ? data.notify_email_errors : true,
        notify_ai_errors: data.notify_ai_errors !== undefined ? data.notify_ai_errors : true,
        notify_transcription_errors: data.notify_transcription_errors !== undefined ? data.notify_transcription_errors : true,
      });
      
      setInterfaceSettings({
        logo: null,
        website_url: data.website_url || '',
        primary_color: data.primary_color || '#ff6b35',
        secondary_color: data.secondary_color || '#ff8c5a',
        accent_color: data.accent_color || '#ffa07a',
        timestamp_theory_color: data.timestamp_theory_color || '#d1ecf1',
        timestamp_practice_color: data.timestamp_practice_color || '#fff3cd',
        timestamp_theory_badge_color: data.timestamp_theory_badge_color || '#17a2b8',
        timestamp_practice_badge_color: data.timestamp_practice_badge_color || '#ffc107',
      });
      
      setPasswordDisplay(data.email_host_password_display || '');
      setApiKeyDisplay(data.openai_api_key_display || '');
    } catch (error) {
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const fetchIpBlacklist = async () => {
    try {
      const response = await axios.get('/api/settings/ip-blacklist/');
      setIpBlacklist(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки блэклиста');
    }
  };

  const fetchFailedAttempts = async () => {
    try {
      const response = await axios.get('/api/settings/failed-login-attempts/');
      setFailedAttempts(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки неудачных попыток входа');
    }
  };

  const handleSaveConnections = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings/system/', connectionSettings);
      toast.success('Настройки подключений сохранены');
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings/system/', notificationSettings);
      toast.success('Настройки уведомлений сохранены');
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInterface = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.keys(interfaceSettings).forEach(key => {
        if (interfaceSettings[key] !== null) {
          formData.append(key, interfaceSettings[key]);
        }
      });
      
      await axios.put('/api/settings/system/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Настройки интерфейса сохранены');
      fetchAllSettings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const testEmailConnection = async () => {
    setTestingEmail(true);
    setEmailTestStatus(null);
    try {
      const response = await axios.post('/api/settings/test/email/', {
        email: notificationSettings.notification_email || connectionSettings.email_host_user,
        ...connectionSettings
      });
      
      if (response.data.success) {
        setEmailTestStatus({ status: 'success', message: response.data.message });
        toast.success(response.data.message);
      } else {
        setEmailTestStatus({ status: 'error', message: response.data.error });
        toast.error(response.data.error);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Ошибка тестирования подключения';
      setEmailTestStatus({ status: 'error', message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingEmail(false);
    }
  };

  const testAIConnection = async () => {
    setTestingAI(true);
    setAiTestStatus(null);
    try {
      const response = await axios.post('/api/settings/test/ai/', {
        ai_api_url: connectionSettings.ai_api_url,
        api_key: connectionSettings.openai_api_key
      });
      
      if (response.data.success) {
        setAiTestStatus({ status: 'success', message: response.data.message });
        toast.success(response.data.message);
      } else {
        setAiTestStatus({ status: 'error', message: response.data.error });
        toast.error(response.data.error);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Ошибка тестирования подключения';
      setAiTestStatus({ status: 'error', message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingAI(false);
    }
  };

  const addIpToBlacklist = async () => {
    if (!newIp.ip_address) {
      toast.error('Введите IP адрес');
      return;
    }
    
    try {
      const response = await axios.post('/api/settings/ip-blacklist/', newIp);
      toast.success('IP адрес добавлен в блэклист');
      setNewIp({ ip_address: '', reason: '' });
      fetchIpBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка добавления IP');
    }
  };

  const updateIpBlacklist = async (ipId, data) => {
    try {
      await axios.put(`/api/settings/ip-blacklist/${ipId}/`, data);
      toast.success('IP адрес обновлен');
      fetchIpBlacklist();
      setEditingIp(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка обновления IP');
    }
  };

  const deleteIpFromBlacklist = async (ipId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот IP адрес из блэклиста?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/settings/ip-blacklist/${ipId}/`);
      toast.success('IP адрес удален из блэклиста');
      fetchIpBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка удаления IP');
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1><FiSettings /> Настройки системы</h1>
      </div>

      <div className="settings-tabs">
        <button 
          className={`tab ${activeTab === 'connections' ? 'active' : ''}`}
          onClick={() => setActiveTab('connections')}
        >
          <FiMail /> Настройки подключения
        </button>
        <button 
          className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          <FiBell /> Уведомления
        </button>
        <button 
          className={`tab ${activeTab === 'interface' ? 'active' : ''}`}
          onClick={() => setActiveTab('interface')}
        >
          <FiMonitor /> Настройки интерфейса
        </button>
        <button 
          className={`tab ${activeTab === 'ip-blacklist' ? 'active' : ''}`}
          onClick={() => setActiveTab('ip-blacklist')}
        >
          <FiShield /> Блэклист IP
        </button>
        <button 
          className={`tab ${activeTab === 'failed-logins' ? 'active' : ''}`}
          onClick={() => setActiveTab('failed-logins')}
        >
          <FiAlertTriangle /> Неудачные попытки входа
        </button>
      </div>

      <div className="settings-content">
        {/* Настройки подключения */}
        {activeTab === 'connections' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Настройки подключения</h2>
              <button className="btn btn-primary" onClick={handleSaveConnections} disabled={saving}>
                {saving ? <><FiLoader className="spinning" /> Сохранение...</> : <><FiSave /> Сохранить</>}
              </button>
            </div>

            {/* SMTP Settings */}
            <div className="subsection">
              <div className="subsection-header">
                <h3><FiMail /> SMTP (Почта)</h3>
                <button 
                  className="btn btn-outline btn-sm" 
                  onClick={testEmailConnection}
                  disabled={testingEmail}
                >
                  {testingEmail ? (
                    <><FiLoader className="spinning" /> Проверка...</>
                  ) : (
                    <><FiRefreshCw /> Тест связи</>
                  )}
                </button>
              </div>
              
              {emailTestStatus && (
                <div className={`status-message ${emailTestStatus.status}`}>
                  {emailTestStatus.status === 'success' ? <FiCheckCircle /> : <FiXCircle />}
                  {emailTestStatus.message}
                </div>
              )}

              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>SMTP сервер *</label>
                    <input
                      type="text"
                      value={connectionSettings.email_host}
                      onChange={(e) => setConnectionSettings({ ...connectionSettings, email_host: e.target.value })}
                      placeholder="mail.example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Порт *</label>
                    <input
                      type="number"
                      value={connectionSettings.email_port}
                      onChange={(e) => setConnectionSettings({ ...connectionSettings, email_port: parseInt(e.target.value) || 465 })}
                      min="1"
                      max="65535"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={connectionSettings.email_use_ssl}
                      onChange={(e) => setConnectionSettings({ ...connectionSettings, email_use_ssl: e.target.checked })}
                    />
                    Использовать SSL
                  </label>
                </div>

                <div className="form-group">
                  <label>Пользователь (Email) *</label>
                  <input
                    type="email"
                    value={connectionSettings.email_host_user}
                    onChange={(e) => setConnectionSettings({ ...connectionSettings, email_host_user: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Пароль</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={connectionSettings.email_host_password}
                      onChange={(e) => setConnectionSettings({ ...connectionSettings, email_host_password: e.target.value })}
                      placeholder={passwordDisplay || "Введите новый пароль или оставьте пустым"}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {passwordDisplay && !connectionSettings.email_host_password && (
                    <div className="form-hint">Текущий пароль: {passwordDisplay}</div>
                  )}
                </div>
              </div>
            </div>

            {/* AI Settings */}
            <div className="subsection">
              <div className="subsection-header">
                <h3><FiCpu /> ИИ (ChadGPT / OpenAI)</h3>
                <button 
                  className="btn btn-outline btn-sm" 
                  onClick={testAIConnection}
                  disabled={testingAI}
                >
                  {testingAI ? (
                    <><FiLoader className="spinning" /> Проверка...</>
                  ) : (
                    <><FiRefreshCw /> Тест связи</>
                  )}
                </button>
              </div>
              
              {aiTestStatus && (
                <div className={`status-message ${aiTestStatus.status}`}>
                  {aiTestStatus.status === 'success' ? <FiCheckCircle /> : <FiXCircle />}
                  {aiTestStatus.message}
                </div>
              )}

              <div className="settings-form">
                <div className="form-group">
                  <label>URL API</label>
                  <input
                    type="url"
                    value={connectionSettings.ai_api_url}
                    onChange={(e) => setConnectionSettings({ ...connectionSettings, ai_api_url: e.target.value })}
                    placeholder="https://ask.chadgpt.ru/api/public/deepseek-v3.1"
                  />
                </div>
                <div className="form-group">
                  <label>API ключ</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={connectionSettings.openai_api_key}
                      onChange={(e) => setConnectionSettings({ ...connectionSettings, openai_api_key: e.target.value })}
                      placeholder={apiKeyDisplay || "Введите новый API ключ или оставьте пустым"}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {apiKeyDisplay && !connectionSettings.openai_api_key && (
                    <div className="form-hint">Текущий ключ: {apiKeyDisplay}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Настройки уведомлений */}
        {activeTab === 'notifications' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Настройки уведомлений</h2>
              <button className="btn btn-primary" onClick={handleSaveNotifications} disabled={saving}>
                {saving ? <><FiLoader className="spinning" /> Сохранение...</> : <><FiSave /> Сохранить</>}
              </button>
            </div>

            <div className="settings-form">
              <div className="form-group">
                <label>Email для уведомлений *</label>
                <input
                  type="email"
                  value={notificationSettings.notification_email}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, notification_email: e.target.value })}
                  placeholder="admin@example.com"
                />
                <div className="form-hint">
                  На этот email будут приходить все системные уведомления
                </div>
              </div>

              <div className="notifications-list">
                <h3>Типы уведомлений</h3>
                
                <div className="notification-item">
                  <div className="notification-info">
                    <strong>Уведомления о попытках взлома</strong>
                    <div className="notification-description">
                      Отправка уведомления при 3 неудачных попытках входа в учетную запись
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.notify_hack_attempts}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_hack_attempts: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <strong>Уведомления об ошибках на сервере</strong>
                    <div className="notification-description">
                      Отправка уведомлений о критических ошибках на сервере
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.notify_server_errors}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_server_errors: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <strong>Уведомления об ошибках соединения с почтой</strong>
                    <div className="notification-description">
                      Отправка уведомлений при проблемах с отправкой email
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.notify_email_errors}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_email_errors: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <strong>Уведомления об ошибках соединения с ИИ</strong>
                    <div className="notification-description">
                      Отправка уведомлений при проблемах с подключением к ИИ API
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.notify_ai_errors}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_ai_errors: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <strong>Уведомления об ошибках создания транскрипции</strong>
                    <div className="notification-description">
                      Отправка уведомлений при ошибках генерации транскрипции видео
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.notify_transcription_errors}
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_transcription_errors: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Настройки интерфейса */}
        {activeTab === 'interface' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Настройки интерфейса</h2>
              <button className="btn btn-primary" onClick={handleSaveInterface} disabled={saving}>
                {saving ? <><FiLoader className="spinning" /> Сохранение...</> : <><FiSave /> Сохранить</>}
              </button>
            </div>

            <div className="settings-form">
              <div className="form-group">
                <label>Логотип</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setInterfaceSettings({ ...interfaceSettings, logo: e.target.files[0] })}
                />
                <div className="form-hint">Загрузите логотип для отображения в интерфейсе</div>
              </div>

              <div className="form-group">
                <label>Ссылка на веб-сайт</label>
                <input
                  type="url"
                  value={interfaceSettings.website_url}
                  onChange={(e) => setInterfaceSettings({ ...interfaceSettings, website_url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Основной цвет</label>
                  <div className="color-input-wrapper">
                    <input
                      type="color"
                      value={interfaceSettings.primary_color}
                      onChange={(e) => setInterfaceSettings({ ...interfaceSettings, primary_color: e.target.value })}
                    />
                    <input
                      type="text"
                      value={interfaceSettings.primary_color}
                      onChange={(e) => setInterfaceSettings({ ...interfaceSettings, primary_color: e.target.value })}
                      placeholder="#ff6b35"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Вторичный цвет</label>
                  <div className="color-input-wrapper">
                    <input
                      type="color"
                      value={interfaceSettings.secondary_color}
                      onChange={(e) => setInterfaceSettings({ ...interfaceSettings, secondary_color: e.target.value })}
                    />
                    <input
                      type="text"
                      value={interfaceSettings.secondary_color}
                      onChange={(e) => setInterfaceSettings({ ...interfaceSettings, secondary_color: e.target.value })}
                      placeholder="#ff8c5a"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Акцентный цвет</label>
                  <div className="color-input-wrapper">
                    <input
                      type="color"
                      value={interfaceSettings.accent_color}
                      onChange={(e) => setInterfaceSettings({ ...interfaceSettings, accent_color: e.target.value })}
                    />
                    <input
                      type="text"
                      value={interfaceSettings.accent_color}
                      onChange={(e) => setInterfaceSettings({ ...interfaceSettings, accent_color: e.target.value })}
                      placeholder="#ffa07a"
                    />
                  </div>
                </div>
              </div>
              <div className="form-hint" style={{ marginTop: '8px', marginBottom: '12px' }}>
                Страница входа использует основную цветовую схему и логотип выше.
              </div>
              <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Цвета временных меток</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Теория (фон)</label>
                  <div className="color-input-wrapper">
                    <input type="color" value={interfaceSettings.timestamp_theory_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_theory_color: e.target.value })} />
                    <input type="text" value={interfaceSettings.timestamp_theory_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_theory_color: e.target.value })} placeholder="#d1ecf1" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Теория (бейдж)</label>
                  <div className="color-input-wrapper">
                    <input type="color" value={interfaceSettings.timestamp_theory_badge_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_theory_badge_color: e.target.value })} />
                    <input type="text" value={interfaceSettings.timestamp_theory_badge_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_theory_badge_color: e.target.value })} placeholder="#17a2b8" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Практика (фон)</label>
                  <div className="color-input-wrapper">
                    <input type="color" value={interfaceSettings.timestamp_practice_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_practice_color: e.target.value })} />
                    <input type="text" value={interfaceSettings.timestamp_practice_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_practice_color: e.target.value })} placeholder="#fff3cd" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Практика (бейдж)</label>
                  <div className="color-input-wrapper">
                    <input type="color" value={interfaceSettings.timestamp_practice_badge_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_practice_badge_color: e.target.value })} />
                    <input type="text" value={interfaceSettings.timestamp_practice_badge_color} onChange={(e) => setInterfaceSettings({ ...interfaceSettings, timestamp_practice_badge_color: e.target.value })} placeholder="#ffc107" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Блэклист IP */}
        {activeTab === 'ip-blacklist' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Блэклист IP адресов</h2>
            </div>

            <div className="add-ip-form">
              <h3>Добавить IP адрес</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>IP адрес *</label>
                  <input
                    type="text"
                    value={newIp.ip_address}
                    onChange={(e) => setNewIp({ ...newIp, ip_address: e.target.value })}
                    placeholder="192.168.1.1"
                  />
                </div>
                <div className="form-group">
                  <label>Причина блокировки</label>
                  <input
                    type="text"
                    value={newIp.reason}
                    onChange={(e) => setNewIp({ ...newIp, reason: e.target.value })}
                    placeholder="Подозрительная активность"
                  />
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <button className="btn btn-primary" onClick={addIpToBlacklist}>
                    <FiPlus /> Добавить
                  </button>
                </div>
              </div>
            </div>

            <div className="ip-blacklist-table">
              <table>
                <thead>
                  <tr>
                    <th>IP адрес</th>
                    <th>Причина</th>
                    <th>Статус</th>
                    <th>Дата добавления</th>
                    <th>Добавил</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {ipBlacklist.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="empty-state">Блэклист пуст</td>
                    </tr>
                  ) : (
                    ipBlacklist.map((ip) => (
                      <tr key={ip.id}>
                        <td><code>{ip.ip_address}</code></td>
                        <td>{ip.reason || '-'}</td>
                        <td>
                          <span className={`status-badge ${ip.is_active ? 'active' : 'inactive'}`}>
                            {ip.is_active ? 'Активна' : 'Неактивна'}
                          </span>
                        </td>
                        <td>{new Date(ip.created_at).toLocaleString('ru-RU')}</td>
                        <td>{ip.created_by_email || '-'}</td>
                        <td>
                          <div className="action-buttons">
                            {editingIp === ip.id ? (
                              <>
                                <button
                                  className="btn-icon"
                                  onClick={() => updateIpBlacklist(ip.id, { is_active: !ip.is_active })}
                                  title="Изменить статус"
                                >
                                  <FiEdit2 />
                                </button>
                                <button
                                  className="btn-icon"
                                  onClick={() => setEditingIp(null)}
                                  title="Отмена"
                                >
                                  <FiXCircle />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn-icon"
                                  onClick={() => setEditingIp(ip.id)}
                                  title="Редактировать"
                                >
                                  <FiEdit2 />
                                </button>
                                <button
                                  className="btn-icon danger"
                                  onClick={() => deleteIpFromBlacklist(ip.id)}
                                  title="Удалить"
                                >
                                  <FiTrash2 />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Неудачные попытки входа */}
        {activeTab === 'failed-logins' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Неудачные попытки входа</h2>
              <button className="btn btn-outline" onClick={fetchFailedAttempts}>
                <FiRefreshCw /> Обновить
              </button>
            </div>

            <div className="failed-attempts-table">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>IP адрес</th>
                    <th>Страна</th>
                    <th>Город</th>
                    <th>Попыток</th>
                    <th>Последняя попытка</th>
                    <th>Уведомление</th>
                  </tr>
                </thead>
                <tbody>
                  {failedAttempts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state">Неудачных попыток входа не найдено</td>
                    </tr>
                  ) : (
                    failedAttempts.map((attempt) => (
                      <tr key={attempt.id} className={attempt.attempt_count >= 3 ? 'high-risk' : ''}>
                        <td>{attempt.email}</td>
                        <td><code>{attempt.ip_address}</code></td>
                        <td>{attempt.country || '-'}</td>
                        <td>{attempt.city || '-'}</td>
                        <td>
                          <span className={`attempt-count ${attempt.attempt_count >= 3 ? 'danger' : ''}`}>
                            {attempt.attempt_count}
                          </span>
                        </td>
                        <td>{new Date(attempt.last_attempt).toLocaleString('ru-RU')}</td>
                        <td>
                          {attempt.is_notified ? (
                            <span className="status-badge success">Отправлено</span>
                          ) : (
                            <span className="status-badge">Не отправлено</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
